import type { ConversationPublic } from '@repo/types/chat';
import type { MessageReadReceipts } from '@repo/types/message';
import type { UserPublic } from '@repo/types/users';

import { GetLastConversationsInfiniteQueryProps } from '@repo/api/chat/chatService';
import { ChatKey } from '@repo/api/chat/keys';
import { useGetLastConversationsInfinite as useGetLastConversationsInfiniteCommon } from '@repo/api/chat/useGetLastConversationsInfinite';
import { InfiniteData, useQueryClient } from '@tanstack/react-query';
import { useEffect } from 'react';

interface UseGetLastConversationsInfiniteProps extends GetLastConversationsInfiniteQueryProps {
  currentChatId: number | null;
  user: UserPublic;
}

export function useGetLastConversationsInfinite(useGetLastConversationsInfiniteProps: UseGetLastConversationsInfiniteProps) {
  const { currentChatId, user, ...props } = useGetLastConversationsInfiniteProps;
  const userId = user.id;

  const queryClient = useQueryClient();

  useEffect(() => {
    const eventSource = new EventSource(import.meta.env.VITE_API_DOMAIN + '/api/chat/sse', {
      withCredentials: true,
    });

    eventSource.onmessage = (event: MessageEvent) => {
      try {
        const data: ConversationPublic = JSON.parse(event.data);
        queryClient.setQueriesData<InfiniteData<ConversationPublic[]>>({ queryKey: [ChatKey.CHAT_LAST_CONVERSATIONS_INFINITE] }, (oldData) => {
          if (!oldData) return oldData;
          let found: boolean = false;
          for (const page of oldData.pages) {
            const existingMessage = page.find((conversation) => conversation.id === data.id);
            if (existingMessage) {
              found = true;
              break;
            }
          }

          if (!found) {
            queryClient.invalidateQueries({
              queryKey: [ChatKey.CHAT_LAST_CONVERSATIONS_INFINITE],
            });
            return oldData;
          }

          const updatedPages: ConversationPublic[][] = oldData.pages.map((page) =>
            page.map((conversation) => {
              if (conversation.id === data.id && currentChatId === data.id) {
                const existingReceipts = data.message?.readReceipts ?? [];
                const alreadyRead = existingReceipts.some((receipt) => receipt.id === userId);
                const newReceipt: MessageReadReceipts = {
                  ...user,
                  messagereadreceipts: { createdAt: new Date().toISOString() },
                };
                const readReceipts = alreadyRead ? existingReceipts : [...existingReceipts, newReceipt];
                return {
                  ...data,
                  message: data.message
                    ? {
                        ...data.message,
                        readReceipts,
                      }
                    : null,
                };
              } else if (conversation.id === data.id) {
                return data.message
                  ? {
                      ...data,
                      message: { ...data.message, readReceipts: [] },
                    }
                  : data;
              }
              return conversation;
            })
          );

          const sortedByActivity: ConversationPublic[] = [...updatedPages.flat()].sort((a, b) => {
            const aDate: string = a.message?.createdAt ?? a.createdAt;
            const bDate: string = b.message?.createdAt ?? b.createdAt;
            return new Date(bDate).getTime() - new Date(aDate).getTime();
          });

          const pages: ConversationPublic[][] = updatedPages.map((page) => {
            const slice: ConversationPublic[] = sortedByActivity.splice(0, page.length);
            return slice;
          });

          return {
            ...oldData,
            pages: pages,
          };
        });
      } catch {
        console.error('Failed to parse event data');
      }
    };

    return () => eventSource.close();
  }, [queryClient, currentChatId, user]);

  return useGetLastConversationsInfiniteCommon(props);
}
